<template>
  <q-card
    class="report-view report-element"
    flat
    dense
    square
    bordered
  >
    <q-card-section
      class="row q-gutter-x-xl"
    >
      <q-card-section
        v-for="column in layoutColumns"
        :key="column"
        class="column justify-between col q-pa-none"
      >
        <q-card-section
          v-for="row in layoutRows"
          :key="row"
          class="row q-pa-none"
          :class="{
            'col q-mb-md': view.viewData[row][column] && view.viewData[row][column].fill,
            'col-auto': view.viewData[row][column] && !view.viewData[row][column].fill
          }"
        >
          <q-card
            v-if="view.viewData[row][column] && view.viewData[row][column].type === 'banner'"
            class="col row justify-between"
            flat
            dense
          >
            <q-card-section
              class="col-auto q-pa-none"
            >
              <img
                :src="logoImageUrl(view.siteData.logo_image)"
                style="max-height: 120px;"
              >
            </q-card-section>
            <q-card-section
              class="col-auto text-h6 q-pa-none q-mt-auto q-mb-md"
              style="text-align: right; font-size: 0.9rem; line-height: 1.5;"
            >
              <span>
                {{ view.siteData.address1 }}
              </span>
              <span>
                <br>{{ view.siteData.city ? view.siteData.city + ', ' : '' }}{{ view.siteData.state ? view.siteData.state + ', ' : '' }}{{ view.siteData.zip }}
              </span>
              <span>
                <br>{{ view.siteData.phone }}
              </span>
              <span>
                <br>{{ view.siteData.friendly_hostname }}
              </span>
            </q-card-section>
          </q-card>
          <q-table
            v-else-if="view.viewData[row][column] && view.viewData[row][column].type === 'table'"
            v-model:pagination="pagination"
            class="col report-table"
            :rows="view.viewData[row][column].data"
            :row-key="rowKey"
            :loading="view.loading"
            :columns="processedColumns(view.viewData[row][column].columns)"
            :rows-per-page-options="[0]"
            flat
            dense
            square
            wrap-cells
            hide-bottom
            no-data-label="No Data"
            :style="cssVars"
          >
            <template #top>
              <div
                class="q-table__title text-h5 q-mx-auto"
              >
                {{ view.viewData[row][column].title }}
              </div>
            </template>

            <template #body-cell="props">
              <q-td
                :props="props"
                :class="cellClass && cellClass(props)"
                :style="{ 'max-width': props.maxWidth || props.col.maxWidth || '225px', 'min-width': props.minWidth || props.col.minWidth || null }"
                auto-width
              >
                <template v-if="props.col.component === 'text'">
                  <span
                    class="text-uppercase text-h6"
                    style="font-size: 1rem;"
                  >
                    {{ props.value.title }}
                  </span>
                  <span class="text-caption">
                    <br>{{ props.value.subtitle }}
                  </span>
                  <span class="text-caption">
                    <br>{{ props.value.description }}
                  </span>
                </template>
                <template v-else-if="props.col.component === 'date-list'">
                  <q-list
                    class="q-py-sm"
                    dense
                  >
                    <q-item
                      v-for="(time, index) in props.value"
                      :key="time + '-' + index"
                      dense
                      class="text-no-wrap"
                      style="min-height: 0; padding: 0;"
                    >
                      <q-item-section>
                        {{ time }}
                      </q-item-section>
                    </q-item>
                  </q-list>
                </template>
                <template v-else-if="props.col.component">
                  <component
                    :is="props.col.component"
                    v-bind="{ props: props }"
                  />
                </template>
                <template v-else>
                  {{ props.value }}
                </template>
              </q-td>
            </template>
          </q-table>
          <div
            v-else-if="view.viewData[row][column] && view.viewData[row][column].type === 'annotation'"
            class="text-left full-width row items-start"
          >
            <q-icon
              class="q-mr-sm q-mt-md"
              size="sm"
              :name="view.viewData[row][column].data.display_icon"
            />
            <div class="text-caption q-mt-md">
              {{ view.viewData[row][column].data.description }}
            </div>
          </div>
          <div
            v-else-if="view.viewData[row][column] && view.viewData[row][column].type === 'cards'"
            class="col column"
            style="text-align: left;"
          >
            <div class="col-auto row">
              <q-card
                v-for="card in view.viewData[row][column].data"
                :key="card.title"
                class="col-6"
                flat
                dense
                square
              >
                <q-card-section
                  class="q-pa-xs"
                >
                  <span
                    class="text-h6"
                    style="font-size: 1.25rem; line-height: 1.5;"
                  >
                    {{ card.title }}
                  </span>
                  <span
                    class="text-h6"
                    style="font-size: 0.9rem; line-height: 1.5;"
                  >
                    <br>{{ card.subtitle }}
                  </span>
                  <span class="text-caption">
                    <br>{{ card.description }}
                  </span>
                </q-card-section>
              </q-card>
            </div>
          </div>
          <q-card
            v-else-if="view.viewData[row][column] && view.viewData[row][column].type === 'flier_block'"
            class="col"
            flat
            dense
          >
            <q-card-section
              v-if="view.viewData[row][column].data.disclaimer"
              class="text-h6 text-negative q-pa-none"
              style="font-size: 1.1rem;"
            >
              <span>
                {{ view.viewData[row][column].data.disclaimer }}
              </span>
            </q-card-section>
            <q-card-section
              v-if="view.viewData[row][column].data.footer_image"
              class="q-mx-auto q-pa-none"
            >
              <img
                :src="footerImageUrl(view.viewData[row][column].data.footer_image)"
              >
            </q-card-section>
          </q-card>
          <q-card
            v-else-if="view.viewData[row][column] &&
              view.viewData[row][column].type === 'price_cards' &&
              view.viewData[row][column].hide_price_card !== true"
            class="col"
            flat
            dense
            square
          >
            <q-card-section
              class="row q-pa-none"
            >
              <table
                v-for="(priceCard, index) in view.viewData[row][column].data"
                :key="priceCard.name"
                :class="index % 2 === 0 && view.viewData[row][column].data.length <= index + 1 ? 'col-12' : 'col-6'"
                style="table-layout: fixed; border: 1px solid black; border-collapse: collapse;"
                :style="{
                  'border-left-width': index % 2 === 0 ? '1px' : '0',
                  'border-top-width': index < 2 ? '1px' : '0'
                }"
              >
                <thead>
                  <tr style="height: 35px;">
                    <th
                      colspan="2"
                      class="text-uppercase text-h6 text-white"
                      style="font-size: 1.05rem; border: 1px solid black; border-collapse: collapse;"
                      :style="{
                        'border-left-width': index % 2 === 0 ? '1px' : '0',
                        'border-top-width': index < 2 ? '1px' : '0',
                        'background-color': view.siteData.secondary_color
                      }"
                    >
                      {{ priceCard.name }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="ticketIndex in Math.round(priceCard.ticket_types.length / 2 + 0.49)"
                    :key="ticketIndex"
                  >
                    <td
                      :colspan="priceCard.ticket_types.length > ticketIndex * 2 - 1 ? '1' : '2'"
                      style="border: 1px solid black; border-collapse: collapse;"
                      :style="{ 'border-left-width': index % 2 === 0 ? '1px' : '0' }"
                    >
                      {{ priceCard.ticket_types[(ticketIndex - 1) * 2].name }}: <i18n-n
                        :value="priceCard.ticket_types[(ticketIndex - 1) * 2].price_cents / 100.0"
                        format="currency"
                        scope="global"
                      />
                    </td>
                    <td
                      v-if="priceCard.ticket_types.length > ticketIndex * 2 - 1"
                      style="border: 1px solid black; border-collapse: collapse;"
                    >
                      {{ priceCard.ticket_types[ticketIndex * 2 - 1].name }}: <i18n-n
                        :value="priceCard.ticket_types[ticketIndex * 2 - 1].price_cents / 100.0"
                        format="currency"
                        scope="global"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                v-if="view.viewData[row][column].notes"
                class="text-caption full-width"
                style="border: 1px solid black; border-top-width: 0; border-collapse: collapse;"
                :style="{ 'background-color': view.siteData.background_color }"
              >
                <span style="white-space: pre-line;">{{ view.viewData[row][column].notes }}</span>
              </div>
            </q-card-section>
          </q-card>
          <q-card
            v-else-if="view.viewData[row][column] && view.viewData[row][column].type === 'showing_badge_legend'"
            class="col"
            flat
            dense
          >
            <q-card-section
              class="q-pa-none"
            >
              <span
                v-for="(item, index) in view.viewData[row][column].data"
                :key="item.text"
              >
                <span v-if="index !== 0">
                  &nbsp;&middot;&nbsp;
                </span>
                <q-icon
                  v-if="item.icon"
                  :name="item.icon"
                  size="sm"
                />
                {{ item.text }}
              </span>
            </q-card-section>
          </q-card>
        </q-card-section>
      </q-card-section>
    </q-card-section>
  </q-card>
</template>

<script>
import { imgixHost } from 'src/config'

export default {
  name: 'ReportView',
  props: {
    view: {
      type: Object,
      required: true
    },
    showFilter: {
      type: Boolean,
      required: false,
      default: undefined
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    sortBy: {
      type: String,
      required: false,
      default: null
    },
    descending: {
      type: Boolean,
      required: false,
      default: true
    },
    cellClass: {
      type: Function,
      required: false,
      default: null
    },
    rowKey: {
      type: String,
      required: false,
      default: 'id'
    },
    printing: {
      type: Boolean,
      required: false,
      default: false
    },
    editing: {
      type: Boolean,
      required: false,
      default: false
    },
    repeatHeaders: {
      type: Boolean,
      required: false,
      default: false
    },
    timeZone: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      model: null,
      filter: '',
      pagination: {
        page: 1,
        rowsPerPage: 0
      },
      loading: false
    }
  },
  computed: {
    layoutRows () {
      return _.times(this.view.layout.rows, (index) => { return index })
    },
    layoutColumns () {
      return _.times(this.view.layout.columns, (index) => { return index })
    },
    shouldShowFilter () {
      return this.showFilter
    },
    paginate () {
      return false
    },
    cssVars () {
      return {
        '--primary-color': this.view.siteData.primary_color
      }
    }
  },
  methods: {
    processedColumns (columns) {
      const result = []

      _.each(columns, column => {
        if (column.component === 'text') {
          result.push({
            name: column.name,
            field: row => row[column.field],
            label: column.label,
            align: column.align,
            component: 'text'
          })
        } else if (column.component === 'date-list') {
          result.push({
            name: column.name,
            field: row => row.dates[column.date],
            label: column.date,
            component: 'date-list'
          })
        }
      })

      return result
    },
    logoImageUrl (image) {
      if (!image) return ''

      return `${imgixHost}/${image}?w=400&auto=format`
    },
    footerImageUrl (image) {
      if (!image) return ''

      return `${imgixHost}/${image}?h=150&auto=format`
    }
  }
}
</script>

<style lang="scss" scoped>
.q-data-table td, .q-data-table th {
  /* don't shorten cell contents */
  white-space: normal !important;
}

</style>

<style lang="scss">
.report-view .report-table {
  table {
    table-layout: fixed;
    border: 0 solid rgba(0, 0, 0, 0.12);
    border-bottom-width: 1px;
  }
  .q-table th:first-child {
    width: 200px;
  }
  .q-table th {
    padding-left: 0;
    white-space: nowrap;
  }
  .q-table td {
    padding-left: 0;
  }
  thead, .q-table__top {
    background-color: var(--primary-color);
    color: white;
  }
}

@media print {
  .report-table thead {
    display: table-header-group;
    page-break-inside: auto
  }
  .report-table.repeat-headers thead {
    display: table-header-group;
    page-break-inside: avoid
  }
  .report-table tbody { page-break-inside: avoid }
  .report-table tr { page-break-inside: avoid }
  .report-table td { page-break-inside: avoid; position: relative; }
}
</style>
